import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import { toPng } from "html-to-image"
import moment from "moment"

const DEFAULT_BRAND = {
    primary: [98, 69, 174],
    secondary: [210, 65, 124],
    text: [42, 45, 55],
    muted: [104, 110, 125],
    border: [225, 226, 232],
    surface: [247, 247, 250],
    card: [255, 255, 255],
    page: [255, 255, 255],
    onAccent: [255, 255, 255],
    success: [43, 135, 89],
    danger: [190, 62, 62],
    chartBackground: "#ffffff",
}

let BRAND = DEFAULT_BRAND

const parseColor = (value, fallback) => {
    const color = String(value || "").trim()
    const hex = color.match(/^#([0-9a-f]{6})$/i)
    if (hex) {
        return [
            parseInt(hex[1].slice(0, 2), 16),
            parseInt(hex[1].slice(2, 4), 16),
            parseInt(hex[1].slice(4, 6), 16),
        ]
    }
    const rgb = color.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i)
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
    return fallback
}

const rgbToHex = color => `#${color.map(value => Math.max(0, Math.min(255, value)).toString(16).padStart(2, "0")).join("")}`

const getThemePalette = () => {
    const rootStyles = getComputedStyle(document.documentElement)
    const read = (name, fallback) => parseColor(rootStyles.getPropertyValue(name), fallback)
    const isDark = rootStyles.colorScheme?.includes("dark")
    const card = read("--surface-card", DEFAULT_BRAND.card)
    return {
        primary: read("--brand-primary", DEFAULT_BRAND.primary),
        secondary: read("--brand-secondary", DEFAULT_BRAND.secondary),
        text: read("--text-primary", DEFAULT_BRAND.text),
        muted: read("--text-secondary", DEFAULT_BRAND.muted),
        border: read("--border-subtle-color", DEFAULT_BRAND.border),
        surface: read("--surface-muted", DEFAULT_BRAND.surface),
        card,
        page: isDark ? read("--surface-app", [21, 21, 26]) : [255, 255, 255],
        onAccent: read("--text-on-accent", DEFAULT_BRAND.onAccent),
        success: read("--status-success", DEFAULT_BRAND.success),
        danger: read("--status-danger", DEFAULT_BRAND.danger),
        chartBackground: rgbToHex(card),
    }
}

const asText = value => {
    if (value === undefined || value === null || value === "") return "-"
    if (typeof value === "boolean") return value ? "Sim" : "Não"
    if (typeof value === "object") {
        if (value.props?.children !== undefined) return asText(value.props.children)
        return JSON.stringify(value)
    }
    return String(value)
}

const normalizeFileName = value => String(value || "relatorio")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()

const fillPageBackground = doc => {
    doc.setFillColor(...BRAND.page)
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), "F")
}

const addPageIfNeeded = (doc, y, requiredHeight, top = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight()
    if (y + requiredHeight <= pageHeight - 18) return y
    doc.addPage()
    fillPageBackground(doc)
    return top
}

const renderFilters = (doc, filters, y, pageWidth, margin) => {
    const contentWidth = pageWidth - (margin * 2)
    const activeFilters = filters?.filter(filter => filter && filter.label) || []
    if (!activeFilters.length) return y

    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(...BRAND.text)
    doc.text("Filtros aplicados", margin, y)
    y += 6

    const columns = 2
    const gap = 6
    const columnWidth = (contentWidth - gap) / columns
    const entries = activeFilters.map(filter => ({
        label: asText(filter.label),
        lines: doc.splitTextToSize(asText(filter.value), columnWidth - 8),
    }))

    for (let index = 0; index < entries.length; index += columns) {
        const row = entries.slice(index, index + columns)
        const rowHeight = Math.max(...row.map(entry => 10 + (entry.lines.length * 4.2)), 16)
        y = addPageIfNeeded(doc, y, rowHeight)

        for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
            const entry = row[columnIndex]
            const x = margin + (columnIndex * (columnWidth + gap))
            doc.setFillColor(...BRAND.surface)
            doc.setDrawColor(...BRAND.border)
            doc.roundedRect(x, y, columnWidth, rowHeight, 2, 2, "FD")
            doc.setFont("helvetica", "bold")
            doc.setFontSize(8)
            doc.setTextColor(...BRAND.muted)
            doc.text(entry.label.toUpperCase(), x + 4, y + 5)
            doc.setFont("helvetica", "normal")
            doc.setFontSize(9)
            doc.setTextColor(...BRAND.text)
            doc.text(entry.lines, x + 4, y + 10)
        }
        y += rowHeight + 3
    }

    return y + 3
}

const renderCards = (doc, cards, y, pageWidth, margin) => {
    if (!cards?.length) return y

    y = addPageIfNeeded(doc, y, 28)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(...BRAND.text)
    doc.text("Indicadores", margin, y)
    y += 6

    const columns = 4
    const gap = 4
    const contentWidth = pageWidth - (margin * 2)
    const cardWidth = (contentWidth - (gap * (columns - 1))) / columns
    const cardHeight = 23

    cards.forEach((card, index) => {
        if (index > 0 && index % columns === 0) y += cardHeight + gap
        if (index % columns === 0) y = addPageIfNeeded(doc, y, cardHeight)

        const column = index % columns
        const x = margin + (column * (cardWidth + gap))
        doc.setFillColor(...BRAND.card)
        doc.setDrawColor(...BRAND.border)
        doc.roundedRect(x, y, cardWidth, cardHeight, 2, 2, "FD")
        doc.setFillColor(...BRAND.primary)
        doc.roundedRect(x, y, 2, cardHeight, 1, 1, "F")

        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor(...BRAND.muted)
        const cardName = doc.splitTextToSize(asText(card.name), cardWidth - 9)
        doc.text(cardName.slice(0, 2), x + 5, y + 6)

        doc.setFont("helvetica", "bold")
        doc.setFontSize(14)
        doc.setTextColor(...BRAND.text)
        doc.text(asText(card.value), x + 5, y + 16)

        if (card.variation !== undefined && card.variation !== null) {
            doc.setFontSize(7)
            doc.setTextColor(...(card.is_positive ? BRAND.success : BRAND.danger))
            doc.text(asText(card.variation), x + cardWidth - 4, y + 16, { align: "right" })
        }
    })

    return y + cardHeight + 8
}

const getChartElements = () => {
    const elements = document.querySelectorAll("[data-dashboard-chart-id]")
    return Array.from(elements).reduce((map, element) => {
        map[element.dataset.dashboardChartId] = element
        return map
    }, {})
}

const renderChartFallback = (doc, chart, x, y, width, height) => {
    const values = (chart.values || []).slice(0, 8)
    doc.setFillColor(...BRAND.card)
    doc.setDrawColor(...BRAND.border)
    doc.roundedRect(x, y, width, height, 2, 2, "FD")
    doc.setFont("helvetica", "bold")
    doc.setFontSize(10)
    doc.setTextColor(...BRAND.text)
    doc.text(doc.splitTextToSize(asText(chart.title), width - 8).slice(0, 2), x + 4, y + 6)
    doc.setFont("helvetica", "normal")
    doc.setFontSize(8)
    values.forEach((item, index) => {
        const line = `${asText(item.name || item.label || item.date)}: ${asText(item.formattedValue ?? item.value)}`
        doc.text(doc.splitTextToSize(line, width - 10)[0], x + 5, y + 18 + (index * 6))
    })
}

const renderCharts = async (doc, charts, y, pageWidth, margin, onProgress) => {
    if (!charts?.length) return y

    y = addPageIfNeeded(doc, y, 18)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(...BRAND.text)
    doc.text("Gráficos", margin, y)
    y += 6

    const chartElements = getChartElements()
    const contentWidth = pageWidth - (margin * 2)
    const gap = 6
    const columnWidth = (contentWidth - gap) / 2
    const maxChartHeight = 82
    const capturedCharts = []

    for (let index = 0; index < charts.length; index++) {
        const chart = charts[index]
        const element = chartElements[String(chart.id)]
        if (!element) {
            capturedCharts.push({ chart, imageData: null, ratio: 1.6 })
            onProgress?.(20 + Math.round(((index + 1) / charts.length) * 60))
            continue
        }

        try {
            const imageData = await toPng(element, {
                backgroundColor: BRAND.chartBackground,
                cacheBust: true,
                pixelRatio: 1.6,
            })
            const ratio = element.offsetWidth / Math.max(element.offsetHeight, 1)
            capturedCharts.push({ chart, imageData, ratio })
        } catch (error) {
            capturedCharts.push({ chart, imageData: null, ratio: 1.6 })
        }
        onProgress?.(20 + Math.round(((index + 1) / charts.length) * 60))
    }

    for (let index = 0; index < capturedCharts.length; index += 2) {
        const row = capturedCharts.slice(index, index + 2).map(item => {
            const naturalHeight = columnWidth / Math.max(item.ratio, 0.1)
            const height = Math.min(naturalHeight, maxChartHeight)
            const width = Math.min(columnWidth, height * item.ratio)
            return { ...item, width, height }
        })
        const rowHeight = Math.max(...row.map(item => item.height), 60)
        y = addPageIfNeeded(doc, y, rowHeight + 6)

        for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
            const item = row[columnIndex]
            const columnX = margin + (columnIndex * (columnWidth + gap))
            const x = columnX + ((columnWidth - item.width) / 2)
            if (item.imageData) {
                doc.addImage(item.imageData, "PNG", x, y, item.width, item.height, undefined, "FAST")
            } else {
                renderChartFallback(doc, item.chart, columnX, y, columnWidth, rowHeight)
            }
        }
        y += rowHeight + 6
    }

    return y + 2
}

const renderTables = (doc, tables, y, margin) => {
    if (!tables?.length) return y

    tables.forEach(table => {
        y = addPageIfNeeded(doc, y, 28)
        doc.setFont("helvetica", "bold")
        doc.setFontSize(12)
        doc.setTextColor(...BRAND.text)
        doc.text(asText(table.title || "Dados detalhados"), margin, y)
        y += 4

        const body = (table.values || []).map(row => (row.value || []).map(asText))
        autoTable(doc, {
            startY: y,
            head: [(table.headers || []).map(asText)],
            body,
            margin: { left: margin, right: margin, top: 18, bottom: 18 },
            theme: "grid",
            styles: {
                font: "helvetica",
                fontSize: 7,
                cellPadding: 2.2,
                textColor: BRAND.text,
                fillColor: BRAND.card,
                lineColor: BRAND.border,
                lineWidth: 0.15,
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: BRAND.primary,
                textColor: BRAND.onAccent,
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: BRAND.surface,
            },
            willDrawPage: hookData => {
                if (hookData.pageNumber > 1) fillPageBackground(doc)
            },
        })
        y = (doc.lastAutoTable?.finalY || y) + 10
    })

    return y
}

const addFooters = (doc, accountName, generatedAt) => {
    const pages = doc.getNumberOfPages()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    for (let page = 1; page <= pages; page++) {
        doc.setPage(page)
        doc.setDrawColor(...BRAND.border)
        doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12)
        doc.setFont("helvetica", "normal")
        doc.setFontSize(7)
        doc.setTextColor(...BRAND.muted)
        doc.text(`${accountName} • Gerado em ${generatedAt}`, 14, pageHeight - 7)
        doc.text(`Página ${page} de ${pages}`, pageWidth - 14, pageHeight - 7, { align: "right" })
    }
}

const generateDashboardPdf = async ({
    title,
    accountName = "Conta",
    generatedBy,
    filters = [],
    data = {},
    onProgress,
}) => {
    onProgress?.(5)
    BRAND = getThemePalette()
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 14
    const generatedAt = moment().format("DD/MM/YYYY [às] HH:mm")

    fillPageBackground(doc)
    doc.setFillColor(...BRAND.primary)
    doc.rect(0, 0, pageWidth, 5, "F")
    doc.setFont("helvetica", "bold")
    doc.setFontSize(20)
    doc.setTextColor(...BRAND.primary)
    doc.text("OKTTO", margin, 18)

    doc.setFontSize(16)
    doc.setTextColor(...BRAND.text)
    doc.text(asText(title), margin, 29)
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor(...BRAND.muted)
    doc.text(asText(accountName), margin, 36)
    doc.text(`Gerado em ${generatedAt}${generatedBy ? ` por ${generatedBy}` : ""}`, pageWidth - margin, 18, { align: "right" })

    let y = 45
    y = renderFilters(doc, filters, y, pageWidth, margin)
    y = renderCards(doc, data.cards, y, pageWidth, margin)
    onProgress?.(20)
    y = await renderCharts(doc, data.charts, y, pageWidth, margin, onProgress)
    onProgress?.(85)
    renderTables(doc, data.tables, y, margin)
    onProgress?.(94)
    addFooters(doc, accountName, generatedAt)

    const date = moment().format("YYYY-MM-DD")
    doc.save(`${normalizeFileName(title)}-${normalizeFileName(accountName)}-${date}.pdf`)
    onProgress?.(100)
}

const getOptionLabels = (selected, options = [], emptyLabel = "Todos") => {
    const values = Array.isArray(selected) ? selected : (selected ? [selected] : [])
    if (!values.length) return emptyLabel
    const labels = values.map(value => {
        const option = options.find(item => String(item?.id) === String(value))
        return option?.value || value
    })
    return labels.join(", ")
}

const formatPeriod = (dateStart, dateEnd) => {
    if (!dateStart && !dateEnd) return "Todo o período"
    const start = dateStart ? moment(dateStart).format("DD/MM/YYYY") : "início"
    const end = dateEnd ? moment(dateEnd).format("DD/MM/YYYY") : "hoje"
    return `${start} a ${end}`
}

export { formatPeriod, generateDashboardPdf, getOptionLabels }
